import { SearchModels, SearchStoreService, locationToPathList } from '$stores';
import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';
import { PopoverModule } from 'primeng/popover';
import { BehaviorSubject, Observable, distinctUntilChanged, filter, forkJoin, map, take } from 'rxjs';
import { Models } from '../../shared/models';
import { SessionStorageService, StorageKeys } from '../../shared/services/session-storage.service';
import { isLargeScreen, listenForLargeScreenDown } from '../../shared/utils';
import { PropertySearchBarComponent } from '../property-search-bar/property-search-bar.component';
import { BedsBathDropdownComponent } from './beds-bath-dropdown/beds-bath-dropdown.component';
import { MoreComponent } from './more/more.component';
import { SaveSearchFormComponent } from './save-search-form/save-search-form.component';
import { MultiSelectOptions } from './toolbar.options';
import { ToolbarService } from './toolbar.service';

interface DisplayValue {
  label: string | undefined;
  value: number | undefined;
}

@UntilDestroy()
@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,

  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    PropertySearchBarComponent,
    MultiSelectModule,
    SharedModule,
    BedsBathDropdownComponent,
    MoreComponent,
    ButtonModule,
    FontAwesomeModule,
    PopoverModule,
    SaveSearchFormComponent,
    AsyncPipe,
  ],
})
export class ToolbarComponent implements OnInit, OnDestroy {
  private readonly searchStore = inject(SearchStoreService);
  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly toolbarService = inject(ToolbarService);
  @ViewChild(MoreComponent, { static: false }) moreComponent!: MoreComponent;

  readonly propertySearchForm = this.toolbarService.propertySearchForm;

  readonly bedsFormControl = this.getControl('MINBEDS');
  readonly bathsFormControl = this.getControl('MINBATHS');

  public readonly largeScreenDownObserver$ = listenForLargeScreenDown();

  /** Are there any active filters */
  readonly hasFilters$ = this.searchStore.queryParamsFilters$.pipe(
    // If at least one filter, return true
    map(filters => !filters || Object.values(filters).some(filter => !!filter)),
    distinctUntilChanged(),
  );

  readonly auctionTypes$: Observable<DisplayValue[]> = this.toolbarService.auctionTypes$;
  readonly propertyTypes$: Observable<DisplayValue[]> = this.toolbarService.propertyTypes$;

  selectedAuctionTypes$ = new BehaviorSubject<(number | undefined)[]>([]);
  selectedPropertyTypes$ = new BehaviorSubject<(number | undefined)[]>([]);

  readonly auctionTypeLabel = 'Auction Types';
  readonly propertyTypeLabel = 'Property Types';

  private readonly locationStorage = this.sessionStorageService.getStorage<SearchModels.Location>(StorageKeys.searchPageLocation);

  propertyType(list: MultiSelectOptions[], val: number) {
    if (!list) {
      return;
    }
    return list.filter(v => v.value === val)[0];
  }

  ngOnInit() {
    this.toolbarService.dispatchFilterChanges().pipe(untilDestroyed(this)).subscribe();

    // Track form changes on mobile to preserve pending changes when closing without applying
    this.propertySearchForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (!isLargeScreen()) {
          this.toolbarService.markPendingChanges();
        }
      });
  }

  ngOnDestroy(): void {
    // Don't clear form on mobile to preserve pending filter changes
    if (isLargeScreen()) {
      this.clearForm();
    }
  }

  applyFiltersForMobile() {
    // We should wait for navigation which will be triggered when
    // this subject below will be triggered:
    //    this._filtersCanBeApplied$.next(true);
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        if (this.locationStorage.hasData()) {
          const pathList = [Models.SearchRouteSlug, ...locationToPathList(this.locationStorage.getData())];
          this.router.navigate(pathList, { queryParamsHandling: 'merge' });
          return;
        }
        this.router.navigate([Models.SearchRouteSlug], { queryParamsHandling: 'merge' });
      });

    this.toolbarService.allowApplyFilter();
  }

  /**
   * Clear and reset all filter data
   */
  clearForm() {
    this.propertySearchForm.reset();

    forkJoin({
      auctionTypes: this.extractTypeValues(this.auctionTypes$),
      propertyTypes: this.extractTypeValues(this.propertyTypes$),
    }).subscribe(({ auctionTypes, propertyTypes }) => {
      this.moreComponent.moreFormGroup.reset();
      this.propertySearchForm.patchValue({ TYPE: [...propertyTypes], CATEGORYLIST: [...auctionTypes] });
    });
  }

  private extractTypeValues(observable$: Observable<DisplayValue[]>) {
    return observable$.pipe(
      map(types => types.map((type: { value: any }) => type.value)),
      take(1),
    );
  }

  private getControl(name: keyof Models.QueryParamsSearchFilters) {
    return (this.propertySearchForm.controls[name] as UntypedFormControl) || null;
  }
}
